import { motion } from 'framer-motion';
import { Users, Mic } from 'lucide-react';

const Speakers = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5 },
  };

  // Placeholder speakers - replace with actual speaker data
  const keynoteSpeakers = [
    {
      name: 'To Be Announced',
      title: 'Keynote Speaker',
      affiliation: 'Institution / Organization',
      topic: 'Topic to be announced',
      photo: null,
    },
    {
      name: 'To Be Announced',
      title: 'Keynote Speaker',
      affiliation: 'Institution / Organization',
      topic: 'Topic to be announced',
      photo: null,
    },
    {
      name: 'To Be Announced',
      title: 'Keynote Speaker',
      affiliation: 'Institution / Organization',
      topic: 'Topic to be announced',
      photo: null,
    },
  ];

  const invitedSpeakers = [
    {
      name: 'To Be Announced',
      title: 'Invited Speaker',
      affiliation: 'Institution / Organization',
      photo: null,
    },
    {
      name: 'To Be Announced',
      title: 'Invited Speaker',
      affiliation: 'Institution / Organization',
      photo: null,
    },
    {
      name: 'To Be Announced',
      title: 'Invited Speaker',
      affiliation: 'Institution / Organization',
      photo: null,
    },
    {
      name: 'To Be Announced',
      title: 'Invited Speaker',
      affiliation: 'Institution / Organization',
      photo: null,
    },
  ];

  return (
    <div data-testid="speakers-page">
      {/* Hero Section */}
      <section className="py-12 md:py-12 mesh-gradient relative overflow-hidden" data-testid="speakers-hero">
        <div className="absolute inset-0 bg-black/5 grid-pattern"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <motion.div {...fadeInUp} className="max-w-4xl">
            <div className="inline-flex items-center space-x-2 bg-[#B6671D]/10 text-[#B6671D]-dark rounded-full px-4 py-2 mb-6">
              <Mic size={20} />
              <span className="font-inter text-sm font-medium">Distinguished Experts</span>
            </div>
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-950 mb-6 tracking-tight">
              Conference <span className="text-[#B6671D]">Speakers</span>
            </h1>
            <p className="font-inter text-lg text-slate-600 leading-relaxed">
              Learn from world-renowned experts in robotics, automation, AI, and smart manufacturing technologies.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Keynote Speakers */}
      <section className="py-20 bg-white" data-testid="keynote-speakers">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div {...fadeInUp} className="mb-12">
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-slate-950 mb-4">
              Keynote Speakers
            </h2>
            <p className="font-inter text-slate-600">
              Distinguished keynote speakers will be announced soon.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {keynoteSpeakers.map((speaker, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-xl hover:border-[#B6671D]/50 transition-all group"
                data-testid={`keynote-speaker-${index}`}
              >
                {/* Photo Section */}
                <div className="flex">
                  {/* Left - Photo */}
                  <div className="w-40 h-48 bg-slate-100 flex-shrink-0 relative">
                    {speaker.photo ? (
                      <img src={speaker.photo} alt={speaker.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Users className="text-slate-300" size={48} />
                      </div>
                    )}
                  </div>
                  
                  {/* Right - Details */}
                  <div className="p-5 flex-1">
                    <h3 className="font-outfit text-xl font-bold text-slate-950 mb-1">{speaker.name}</h3>
                    <p className="font-inter text-[#B6671D] text-sm font-medium mb-1">{speaker.title}</p>
                    <p className="font-inter text-slate-600 text-sm mb-3">{speaker.affiliation}</p>
                    {speaker.topic && (
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <p className="font-inter text-xs text-slate-500 uppercase tracking-wide mb-1">Talk Topic</p>
                        <p className="font-inter text-sm text-slate-700">{speaker.topic}</p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Invited Speakers */}
      <section className="py-20 bg-slate-50" data-testid="invited-speakers">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div {...fadeInUp} className="mb-12">
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-slate-950 mb-4">
              Invited Speakers
            </h2>
            <p className="font-inter text-slate-600">
              Invited speakers from academia and industry will be announced soon.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {invitedSpeakers.map((speaker, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-lg hover:border-[#B6671D]/50 transition-all"
                data-testid={`invited-speaker-${index}`}
              >
                {/* Photo */}
                <div className="aspect-square bg-slate-100 relative">
                  {speaker.photo ? (
                    <img src={speaker.photo} alt={speaker.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Users className="text-slate-300" size={48} />
                    </div>
                  )}
                </div>
                
                {/* Details */}
                <div className="p-4 text-center">
                  <h3 className="font-outfit text-lg font-bold text-slate-950 mb-1">{speaker.name}</h3>
                  <p className="font-inter text-[#B6671D] text-sm font-medium mb-1">{speaker.title}</p>
                  <p className="font-inter text-slate-600 text-xs">{speaker.affiliation}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};

export default Speakers;
